import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, MessageSquare, ArrowLeft, Home, Quote } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicTestimonials() {
  const [data, setData] = useState({ items: [], stats: { count: 0, average_rating: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/testimonials/public?limit=200`)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 py-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white font-bold">
            <Home className="w-5 h-5" />
            AllStillas
          </Link>
          <Link to="/">
            <Button variant="outline" className="border-white/20 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake
            </Button>
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full mb-4">
            <MessageSquare className="w-4 h-4 text-[#F7931A]" />
            <span className="text-[#F7931A] font-semibold text-sm uppercase tracking-wider">
              Kundeanmeldelser
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Unbounded'] font-bold text-white mb-4">
            Hva kundene våre sier
          </h1>
          <p className="text-[#94A3B8] text-base md:text-lg max-w-2xl mx-auto">
            Ekte tilbakemeldinger fra fornøyde kunder som har valgt AllStillas
          </p>

          {data.stats.count > 0 && (
            <div className="inline-flex items-center gap-3 mt-8 px-6 py-4 bg-gradient-to-r from-yellow-500/10 to-[#F7931A]/10 border border-yellow-500/30 rounded-2xl">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-6 h-6 ${
                      n <= Math.round(data.stats.average_rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-[#475569]"
                    }`}
                  />
                ))}
              </div>
              <span className="text-2xl font-bold text-white">
                {data.stats.average_rating} / 5
              </span>
              <span className="text-[#94A3B8]">
                ({data.stats.count}{" "}
                {data.stats.count === 1 ? "anmeldelse" : "anmeldelser"})
              </span>
            </div>
          )}
        </div>

        {/* Reviews */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-[#94A3B8] opacity-50" />
              <p className="text-[#94A3B8]">Ingen anmeldelser ennå</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.items.map((t) => (
              <Card
                key={t.testimonial_id}
                className="bg-[#0A0A0A] border-white/10 hover:border-[#F7931A]/50 transition-colors"
                data-testid={`public-testimonial-${t.testimonial_id}`}
              >
                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-[#F7931A]/40 mb-3" />
                  <div className="flex items-center gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${
                          n <= t.rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-[#475569]"
                        }`}
                      />
                    ))}
                  </div>
                  {t.comment && (
                    <p className="text-[#CBD5E1] mb-4 leading-relaxed">"{t.comment}"</p>
                  )}
                  <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F7931A] to-[#FFCD00] flex items-center justify-center text-white font-bold flex-shrink-0">
                      {t.name[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t.name}</p>
                      <p className="text-[#94A3B8] text-xs">
                        {new Date(t.created_at).toLocaleDateString("no-NO", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
